// Pure maths for the 對齊地圖 view. Compass bearings (clockwise from
// north, D-28) appear here only through geo.js's ref.rotationDeg.
import { toLatLng, toSite } from "../../../../utils/geo";
import { MIN_DIMENSION } from "./constants";

const deg = (r) => (r * 180) / Math.PI;
// Rounded first, so -1e-12 reads as 0 rather than 359.999999999.
const mod360 = (d) => (((Math.round(d * 1e8) / 1e8) % 360) + 360) % 360;

export const HANDLE_HIT_PX = 14;
// Taiwan default view when nothing is set and nothing was pasted.
export const DEFAULT_CENTRE = { lat: 23.97, lng: 120.97 };

// ---------------------------------------------------------------- paste box
/** "24.147731, 120.673648" (Google Maps copy) -> {lat, lng} or {error}. */
export const parseLatLng = (text) => {
  const parts = String(text ?? "")
    .trim()
    .split(/[\s,，]+/)
    .filter(Boolean);
  if (parts.length !== 2) {
    return { error: "請貼上「緯度, 經度」，例如 24.147731, 120.673648" };
  }
  const [lat, lng] = parts.map(Number);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return { error: "座標必須是數字" };
  }
  if (lat < -90 || lat > 90) return { error: "緯度需介於 -90 到 90" };
  if (lng < -180 || lng > 180) return { error: "經度需介於 -180 到 180" };
  return { lat, lng };
};

// ---------------------------------------------------------------- anchoring
/**
 * Anchor `rotationDeg` so site point `siteXY` lands on `latLng`: the origin
 * whose toLatLng(ref, x, y) is `latLng`. Fixed-point refinement, since the
 * metres-per-degree scales depend on the origin's latitude.
 */
export const anchorRef = (rotationDeg, siteXY, latLng) => {
  let ref = { lat: latLng[0], lng: latLng[1], rotationDeg };
  for (let i = 0; i < 4; i += 1) {
    const [x, y] = toSite(ref, latLng[0], latLng[1]);
    const [lat, lng] = toLatLng(ref, x - siteXY[0], y - siteXY[1]);
    ref = { lat, lng, rotationDeg };
  }
  return ref;
};

// ---------------------------------------------------------------- the site on the map
/**
 * The image rectangle in site metres, letterboxed like the canvas: CSS
 * object-fit contain, object-position top left, inside a floor-shaped box
 * whose top-left is (0, H) in y-up metres.
 */
export const letterboxSiteRect = (image, floor) => {
  const s = Math.min(floor.width / image.width, floor.height / image.height);
  return {
    left: 0,
    right: image.width * s,
    top: floor.height,
    bottom: floor.height - image.height * s,
  };
};

const rectToLatLngs = (r, ref) =>
  [
    [r.left, r.top],
    [r.right, r.top],
    [r.right, r.bottom],
    [r.left, r.bottom],
  ].map(([x, y]) => toLatLng(ref, x, y));

/** MapLibre image-source corners as [lat, lng]: TL, TR, BR, BL. */
export const imageCorners = (image, floor, ref) =>
  rectToLatLngs(letterboxSiteRect(image, floor), ref);

export const floorCorners = (floor, ref) =>
  rectToLatLngs(
    { left: 0, right: floor.width, top: floor.height, bottom: 0 },
    ref,
  );

// ---------------------------------------------------------------- drag and rotate
/** Rigid move: the origin follows the pointer's lat/lng delta. */
export const shiftRef = (ref, from, to) => ({
  ...ref,
  lat: ref.lat + (to[0] - from[0]),
  lng: ref.lng + (to[1] - from[1]),
});

/** The rotate handle sits above the top edge, on the +y axis through the centre. */
export const handleSite = (floor) => [
  floor.width / 2,
  floor.height + 0.12 * Math.max(floor.width, floor.height),
];

export const centreOf = (floor) => [floor.width / 2, floor.height / 2];

// ---------------------------------------------------------------- the centre
/** The site centre's [lat, lng]. */
export const siteCentre = (ref, floor) => toLatLng(ref, ...centreOf(floor));

/** A pasted or clicked point is the site CENTRE; the ref keeps the 0,0 corner. */
export const placeCentre = (rotationDeg, latLng, floor) =>
  anchorRef(rotationDeg, centreOf(floor), latLng);

/** Absolute bearing, pivoting on the centre. */
export const setBearing = (ref, floor, rotationDeg) =>
  placeCentre(mod360(rotationDeg), siteCentre(ref, floor), floor);

export const turnRef = (ref, floor, deltaDeg) =>
  setBearing(ref, floor, ref.rotationDeg + deltaDeg);

/** Rigid move by metres east (dE) and north (dN); the bearing is kept. */
export const nudgeRef = (ref, dE, dN) => {
  const [lat, lng] = toLatLng({ ...ref, rotationDeg: 0 }, dE, dN);
  return { ...ref, lat, lng };
};

const NUDGE = {
  ArrowRight: [1, 0],
  ArrowLeft: [-1, 0],
  ArrowUp: [0, 1],
  ArrowDown: [0, -1],
};
const TURN = { BracketLeft: -1, BracketRight: 1 };
const SCALE = { Minus: -1, Equal: 1 };

/**
 * Arrows nudge 0.1 m (Shift 1 m) on the north-up map; [ ] turn 0.1° (Shift
 * 1°); - = scale uniformly by 0.1 % (Shift 1 %).
 */
export const keyAction = (e) => {
  if (e.ctrlKey || e.metaKey || e.altKey) return null;
  const step = e.shiftKey ? 1 : 0.1;
  if (NUDGE[e.key]) return { nudge: NUDGE[e.key].map((v) => v * step + 0) };
  if (TURN[e.code]) return { turn: TURN[e.code] * step };
  if (SCALE[e.code]) return { scale: 1 + (SCALE[e.code] * step) / 100 };
  return null;
};

/** Turn the site so its +y axis points at `pointer`, pivoting on the centre. */
export const rotateRefToward = (ref, floor, pointer) => {
  const centre = toLatLng(ref, ...centreOf(floor));
  const [dE, dN] = toSite(
    { lat: centre[0], lng: centre[1], rotationDeg: 0 },
    pointer[0],
    pointer[1],
  );
  return anchorRef(mod360(deg(Math.atan2(dE, dN))), centreOf(floor), centre);
};

// ---------------------------------------------------------------- scale and stretch
// Every resize pivots on the site CENTRE, the same as rotation: the centre and
// the bearing stay, and the stored 0,0 corner is recomputed from centre + size
// + angle. Corners scale W and H by one factor; edge midpoints stretch one
// axis. Neither side goes below MIN_DIMENSION. Named in site axes: n is the
// top edge (y = H), e the right edge (x = W).
const SIDES = {
  nw: [-1, 1],
  n: [0, 1],
  ne: [1, 1],
  e: [1, 0],
  se: [1, -1],
  s: [0, -1],
  sw: [-1, -1],
  w: [-1, 0],
};

/** The eight resize handles: four corners and four edge midpoints. */
export const resizeHandles = (floor) =>
  Object.entries(SIDES).map(([kind, [sx, sy]]) => ({
    kind,
    site: [((1 + sx) * floor.width) / 2, ((1 + sy) * floor.height) / 2],
  }));

/** The site at size `next`, around the same centre and at the same angle. */
export const resizeFrame = (ref, floor, next) => ({
  ref: placeCentre(ref.rotationDeg, siteCentre(ref, floor), next),
  floor: next,
});

const minFactor = (floor) =>
  MIN_DIMENSION / Math.min(floor.width, floor.height);

/** Uniform scale by `factor` (the - = keys), clamped at MIN_DIMENSION. */
export const scaleFrame = (ref, floor, factor) => {
  const f = Math.max(factor, Math.min(1, minFactor(floor)));
  return resizeFrame(ref, floor, {
    width: floor.width * f,
    height: floor.height * f,
  });
};

const stretch = (size, delta) => Math.max(size + 2 * delta, MIN_DIMENSION);

/**
 * A handle drag from `start` to `point` ([lat,lng]), measured in the site's
 * own axes from where it was grabbed, so the first move never jumps. A corner
 * uses the drag's projection on its diagonal, so W and H keep their ratio.
 */
export const dragResize = ({ ref, floor, kind, start, point }) => {
  const [sx, sy] = SIDES[kind];
  const [ax, ay] = toSite(ref, start[0], start[1]);
  const [bx, by] = toSite(ref, point[0], point[1]);
  const [dx, dy] = [(bx - ax) * sx, (by - ay) * sy];
  if (sx && sy) {
    const [hw, hh] = [floor.width / 2, floor.height / 2];
    const f = 1 + (dx * hw + dy * hh) / (hw * hw + hh * hh);
    return scaleFrame(ref, floor, f);
  }
  return resizeFrame(ref, floor, {
    width: sx ? stretch(floor.width, dx) : floor.width,
    height: sy ? stretch(floor.height, dy) : floor.height,
  });
};

const CURSORS = ["ns-resize", "nesw-resize", "ew-resize", "nwse-resize"];

/** The resize cursor for a handle, from its on-screen direction (north up). */
export const resizeCursor = (kind, floor, rotationDeg) => {
  const [sx, sy] = SIDES[kind];
  const local = deg(Math.atan2(sx * floor.width, sy * floor.height));
  const axis = (mod360(rotationDeg + local) + 22.5) % 180;
  return CURSORS[Math.floor(axis / 45)];
};

// ---------------------------------------------------------------- hit test
/**
 * What a pointer at `point` ([lat,lng]) grabs: "rotate", a resize handle's
 * kind, "move" or null. The rotate handle wins, then corners, then edges.
 */
export const hitTest = ({
  ref,
  floor,
  point,
  project,
  radius = HANDLE_HIT_PX,
}) => {
  const here = project(point);
  const near = (site) => {
    const p = project(toLatLng(ref, ...site));
    return Math.hypot(p.x - here.x, p.y - here.y) <= radius;
  };
  if (near(handleSite(floor))) return "rotate";
  const handles = resizeHandles(floor);
  const corner = handles.find((h) => h.kind.length === 2 && near(h.site));
  const edge = handles.find((h) => h.kind.length === 1 && near(h.site));
  if (corner || edge) return (corner || edge).kind;
  const [x, y] = toSite(ref, point[0], point[1]);
  return x >= 0 && x <= floor.width && y >= 0 && y <= floor.height
    ? "move"
    : null;
};
